import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, InputGroup, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconCardHeader from '../common/FalconCardHeader';
import AdvanceTable from '../common/advance-table/AdvanceTable';
import AdvanceTablePagination from '../common/advance-table/AdvanceTablePagination';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import { createColumnHelper } from '@tanstack/react-table';
import DatePicker from 'react-datepicker';

const AuditLog = () => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // Última semana
    endDate: new Date()
  });
  const [filterType, setFilterType] = useState('all');
  const [filterUser, setFilterUser] = useState('');

  const auditLogs = [
    {
      id: 1,
      timestamp: '2024-01-15 14:30:25',
      user: 'Juan Rodriguez',
      userEmail: 'juan.rodriguez@galaxymetrics.com',
      action: 'user.create',
      actionDisplay: 'Usuario creado',
      target: 'María González',
      targetType: 'user',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
      success: true,
      details: 'Nuevo usuario creado en el sistema con rol Editor'
    },
    {
      id: 2,
      timestamp: '2024-01-15 12:15:40',
      user: 'Juan Rodriguez',
      userEmail: 'juan.rodriguez@galaxymetrics.com',
      action: 'role.update',
      actionDisplay: 'Rol modificado',
      target: 'Editor',
      targetType: 'role',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
      success: true,
      details: 'Permisos del rol Editor actualizados - agregado orders.delete'
    },
    {
      id: 3,
      timestamp: '2024-01-15 10:45:18',
      user: 'Pedro Martínez',
      userEmail: 'pedro.martinez@galaxymetrics.com',
      action: 'auth.login',
      actionDisplay: 'Inicio de sesión',
      target: 'Sistema',
      targetType: 'system',
      ipAddress: '192.168.1.50',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
      success: true,
      details: 'Inicio de sesión exitoso'
    },
    {
      id: 4,
      timestamp: '2024-01-15 09:22:33',
      user: 'María González',
      userEmail: 'maria.gonzalez@galaxymetrics.com',
      action: 'order.create',
      actionDisplay: 'Orden creada',
      target: 'ORD-1705318953-abc123def',
      targetType: 'order',
      ipAddress: '192.168.1.75',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
      success: true,
      details: 'Nueva orden publicitaria creada para cliente CALOX'
    },
    {
      id: 5,
      timestamp: '2024-01-14 16:20:15',
      user: 'Juan Rodriguez',
      userEmail: 'juan.rodriguez@galaxymetrics.com',
      action: 'user.disable',
      actionDisplay: 'Usuario deshabilitado',
      target: 'Ana López',
      targetType: 'user',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
      success: true,
      details: 'Usuario deshabilitado por inactividad prolongada'
    },
    {
      id: 6,
      timestamp: '2024-01-14 14:55:22',
      user: 'Sistema',
      userEmail: 'system@galaxymetrics.com',
      action: 'auth.failed',
      actionDisplay: 'Intento fallido',
      target: 'pedro.martinez@galaxymetrics.com',
      targetType: 'auth',
      ipAddress: '203.0.113.42',
      userAgent: 'Mozilla/5.0 (X11; Linux x86_64)',
      success: false,
      details: 'Intento de acceso fallido - contraseña incorrecta (3 intentos)'
    }
  ];

  const actionTypes = [
    { value: 'all', label: 'Todas las acciones' },
    { value: 'user', label: 'Gestión de usuarios' },
    { value: 'role', label: 'Gestión de roles' },
    { value: 'auth', label: 'Autenticación' },
    { value: 'order', label: 'Gestión de órdenes' },
    { value: 'system', label: 'Sistema' }
  ];

  const columnHelper = createColumnHelper();

  const columns = useMemo(
    () => [
      columnHelper.accessor('timestamp', {
        header: 'Fecha y Hora',
        cell: ({ getValue }) => {
          const date = new Date(getValue());
          return (
            <div>
              <div className="fw-semibold">
                {date.toLocaleDateString('es-ES')}
              </div>
              <small className="text-muted">
                {date.toLocaleTimeString('es-ES')}
              </small>
            </div>
          );
        }
      }),
      columnHelper.accessor('user', {
        header: 'Usuario',
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{row.original.user}</div>
            <small className="text-muted">{row.original.userEmail}</small>
          </div>
        )
      }),
      columnHelper.accessor('action', {
        header: 'Acción',
        cell: ({ row }) => {
          const { action, actionDisplay, success } = row.original;
          const getActionIcon = (actionType) => {
            if (actionType.startsWith('user')) return 'users';
            if (actionType.startsWith('role')) return 'user-shield';
            if (actionType.startsWith('auth')) return 'sign-in-alt';
            if (actionType.startsWith('order')) return 'shopping-cart';
            return 'cog';
          };
          
          const getActionColor = (actionType, success) => {
            if (!success) return 'danger';
            if (actionType.includes('create')) return 'success';
            if (actionType.includes('update')) return 'warning';
            if (actionType.includes('delete') || actionType.includes('disable')) return 'danger';
            return 'info';
          };

          return (
            <Badge bg={getActionColor(action, success)} className="fs-11">
              <FontAwesomeIcon icon={getActionIcon(action)} className="me-1" />
              {actionDisplay}
            </Badge>
          );
        }
      }),
      columnHelper.accessor('target', {
        header: 'Objetivo',
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{row.original.target}</div>
            <small className="text-muted text-capitalize">{row.original.targetType}</small>
          </div>
        )
      }),
      columnHelper.accessor('ipAddress', {
        header: 'IP / Ubicación',
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold font-monospace fs-11">{row.original.ipAddress}</div>
            <small className="text-muted">
              {row.original.ipAddress.startsWith('192.168') ? '🏠 Interna' : '🌐 Externa'}
            </small>
          </div>
        )
      }),
      columnHelper.accessor('success', {
        header: 'Estado',
        cell: ({ getValue }) => (
          <Badge bg={getValue() ? 'success' : 'danger'} className="fs-11">
            <FontAwesomeIcon 
              icon={getValue() ? 'check-circle' : 'times-circle'} 
              className="me-1" 
            />
            {getValue() ? 'Exitoso' : 'Fallido'}
          </Badge>
        )
      }),
      columnHelper.accessor('details', {
        header: 'Detalles',
        cell: ({ getValue }) => (
          <div 
            className="text-muted fs-11" 
            style={{ maxWidth: '200px' }}
            title={getValue()}
          >
            {getValue().length > 50 ? `${getValue().substring(0, 50)}...` : getValue()}
          </div>
        )
      })
    ],
    []
  );

  const filteredLogs = useMemo(() => {
    return auditLogs.filter(log => {
      const matchesType = filterType === 'all' || log.action.startsWith(filterType);
      const matchesUser = !filterUser || 
        log.user.toLowerCase().includes(filterUser.toLowerCase()) ||
        log.userEmail.toLowerCase().includes(filterUser.toLowerCase());
      
      const logDate = new Date(log.timestamp);
      const matchesDate = logDate >= dateRange.startDate && logDate <= dateRange.endDate;
      
      return matchesType && matchesUser && matchesDate;
    });
  }, [auditLogs, filterType, filterUser, dateRange]);

  const exportLogs = () => {
    const csvContent = [
      ['Fecha', 'Usuario', 'Acción', 'Objetivo', 'IP', 'Estado', 'Detalles'].join(','),
      ...filteredLogs.map(log => [
        log.timestamp,
        log.user,
        log.actionDisplay,
        log.target,
        log.ipAddress,
        log.success ? 'Exitoso' : 'Fallido',
        `"${log.details}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Configurar la tabla usando el hook existente
  const table = useAdvanceTable({
    data: filteredLogs,
    columns,
    sortable: true,
    pagination: true,
    perPage: 25
  });

  return (
    <Container fluid className="px-3">
      <div className="pb-4">
        {/* Header */}
        <div className="mb-4">
          <Row className="align-items-center">
            <Col>
              <h2 className="mb-2">
                <FontAwesomeIcon icon="list" className="me-2 text-primary" />
                Registro de Auditoría
              </h2>
              <h5 className="text-700 fw-semi-bold">
                Monitoreo de actividades y seguridad del sistema
              </h5>
            </Col>
            <Col xs="auto">
              <Button variant="outline-primary" onClick={exportLogs}>
                <FontAwesomeIcon icon="download" className="me-1" />
                Exportar CSV
              </Button>
            </Col>
          </Row>
        </div>

        {/* Estadísticas rápidas */}
        <Row className="g-3 mb-4">
          <Col lg={3} md={6}>
            <Card className="bg-primary text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{filteredLogs.length}</h4>
                    <small>Eventos Registrados</small>
                  </div>
                  <FontAwesomeIcon icon="list" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="bg-success text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{filteredLogs.filter(l => l.success).length}</h4>
                    <small>Acciones Exitosas</small>
                  </div>
                  <FontAwesomeIcon icon="check-circle" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="bg-danger text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{filteredLogs.filter(l => !l.success).length}</h4>
                    <small>Intentos Fallidos</small>
                  </div>
                  <FontAwesomeIcon icon="exclamation-triangle" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="bg-info text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="text-white mb-0">{new Set(filteredLogs.map(l => l.user)).size}</h4>
                    <small>Usuarios Activos</small>
                  </div>
                  <FontAwesomeIcon icon="users" size="2x" className="opacity-50" />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtros */}
        <Card className="shadow-sm mb-4">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col lg={3} md={6}>
                <Form.Label className="fw-semibold">
                  <FontAwesomeIcon icon="calendar" className="me-1" />
                  Fecha Desde
                </Form.Label>
                <DatePicker
                  selected={dateRange.startDate}
                  onChange={(date) => setDateRange(prev => ({ ...prev, startDate: date }))}
                  className="form-control"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Fecha desde..."
                />
              </Col>
              <Col lg={3} md={6}>
                <Form.Label className="fw-semibold">
                  <FontAwesomeIcon icon="calendar" className="me-1" />
                  Fecha Hasta
                </Form.Label>
                <DatePicker
                  selected={dateRange.endDate}
                  onChange={(date) => setDateRange(prev => ({ ...prev, endDate: date }))}
                  className="form-control"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Fecha hasta..."
                />
              </Col>
              <Col lg={3} md={6}>
                <Form.Label className="fw-semibold">
                  <FontAwesomeIcon icon="filter" className="me-1" />
                  Tipo de Acción
                </Form.Label>
                <Form.Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  {actionTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={3} md={6}>
                <Form.Label className="fw-semibold">
                  <FontAwesomeIcon icon="search" className="me-1" />
                  Buscar Usuario
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Nombre o email..."
                    value={filterUser}
                    onChange={(e) => setFilterUser(e.target.value)}
                  />
                  {filterUser && (
                    <Button 
                      variant="outline-secondary"
                      onClick={() => setFilterUser('')}
                    >
                      <FontAwesomeIcon icon="times" />
                    </Button>
                  )}
                </InputGroup>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tabla de Logs */}
        <Card className="shadow-sm">
          <FalconCardHeader
            title={`Registro de Actividad (${filteredLogs.length} eventos)`}
            endEl={
              <div className="d-flex gap-2">
                <Dropdown>
                  <Dropdown.Toggle variant="falcon-default" size="sm">
                    <FontAwesomeIcon icon="cog" className="me-1" />
                    Opciones
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={exportLogs}>
                      <FontAwesomeIcon icon="download" className="me-2" />
                      Exportar CSV
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => window.print()}>
                      <FontAwesomeIcon icon="print" className="me-2" />
                      Imprimir
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item>
                      <FontAwesomeIcon icon="redo" className="me-2" />
                      Actualizar
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            }
          />
          <Card.Body className="p-0">
            <AdvanceTableProvider {...table}>
              <AdvanceTable
                headerClassName="bg-light text-900"
                bodyClassName="bg-white"
                tableProps={{
                  size: 'sm',
                  className: 'fs-10 mb-0'
                }}
              />
              <div className="px-3 pb-3">
                <AdvanceTablePagination />
              </div>
            </AdvanceTableProvider>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default AuditLog; 